import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Bus,
  Calendar,
  Users,
  MapPin,
  Phone
} from 'lucide-react';
import Swal from 'sweetalert2';
import VehicleModal from '../components/VehicleModal';
import { vehicleApi } from '../services/api';

const VehicleManagement = () => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBrand, setFilterBrand] = useState('');
  const [filterBusType, setFilterBusType] = useState('');
  const [filterAvailability, setFilterAvailability] = useState('true');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState(''); // New state for API errors

  // Load vehicles from API
  const loadVehicles = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Build filters object, only including defined values
      const filters = {};
      
      if (searchTerm && searchTerm.trim() !== '') {
        filters.searchTerm = searchTerm.trim();
      }
      
      if (filterBrand && filterBrand.trim() !== '') {
        filters.brand = filterBrand.trim();
      }
      
      if (filterBusType && filterBusType.trim() !== '') {
        filters.busType = filterBusType.trim();
      }
      
      if (filterAvailability && filterAvailability !== '') {
        filters.isAvailable = filterAvailability === 'true';
      }
      
      // Always include pagination
      filters.pageNumber = currentPage;
      filters.pageSize = 10;

      console.log('Sending filters:', filters); // Debug log

      const response = await vehicleApi.getVehicles(filters);
      
      setVehicles(response.vehicles || []);
      setTotalCount(response.totalCount || 0);
      setTotalPages(response.totalPages || 1);
    } catch (err) {
      console.error('Error loading vehicles:', err);
      setError('Failed to load vehicles. Please try again.');
      setVehicles([]);
    } finally {
      setLoading(false);
    }
  };

  // Load all vehicles (including unavailable) when needed
  const loadAllVehicles = async () => {
    try {
      setLoading(true);
      setError('');
      
      const filters = {
        pageNumber: currentPage,
        pageSize: 10
      };
      
      if (searchTerm && searchTerm.trim() !== '') {
        filters.searchTerm = searchTerm.trim();
      }
      
      if (filterBrand && filterBrand.trim() !== '') {
        filters.brand = filterBrand.trim();
      }
      
      if (filterBusType && filterBusType.trim() !== '') {
        filters.busType = filterBusType.trim();
      }

      const response = await vehicleApi.getVehicles(filters);
      
      setVehicles(response.vehicles || []);
      setTotalCount(response.totalCount || 0);
      setTotalPages(response.totalPages || 1);
    } catch (err) {
      console.error('Error loading all vehicles:', err);
      setError('Failed to load vehicles. Please try again.');
      setVehicles([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVehicles();
  }, [currentPage, searchTerm, filterBrand, filterBusType, filterAvailability]);

  // Initial load - get only active vehicles by default
  useEffect(() => {
    const loadInitialVehicles = async () => {
      try {
        setLoading(true);
        setError('');
        
        // Load only active vehicles initially for better UX
        const response = await vehicleApi.getVehicles({
          pageNumber: 1,
          pageSize: 10,
          isAvailable: true
        });
        
        setVehicles(response.vehicles || []);
        setTotalCount(response.totalCount || 0);
        setTotalPages(response.totalPages || 1);
      } catch (err) {
        console.error('Error loading initial vehicles:', err);
        setError('Failed to load vehicles. Please try again.');
        setVehicles([]);
      } finally {
        setLoading(false);
      }
    };

    loadInitialVehicles();
  }, []); // Empty dependency array - only run once on mount

  // Apply frontend filtering for display only (not for API calls)
  const filteredVehicles = vehicles.filter(vehicle => {
    // Only apply search filter if there's a search term
    if (searchTerm && searchTerm.trim() !== '') {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = vehicle.registrationNumber.toLowerCase().includes(searchLower) ||
                           vehicle.model.toLowerCase().includes(searchLower) ||
                           vehicle.brand.toLowerCase().includes(searchLower);
      if (!matchesSearch) return false;
    }
    
    // Only apply brand filter if there's a brand selected
    if (filterBrand && filterBrand.trim() !== '') {
      if (vehicle.brand !== filterBrand) return false;
    }
    
    // Only apply bus type filter if there's a bus type selected
    if (filterBusType && filterBusType.trim() !== '') {
      if (vehicle.busType !== filterBusType) return false;
    }
    
    // Only apply availability filter if there's a status selected
    if (filterAvailability && filterAvailability !== '') {
      const isAvailable = filterAvailability === 'true';
      if (vehicle.isAvailable !== isAvailable) return false;
    }
    
    return true;
  });

  // Handle availability filter change
  const handleAvailabilityFilterChange = async (availability) => {
    setFilterAvailability(availability);
    
    if (availability === '' || availability === 'true') {
      // Show only available vehicles
      await loadVehicles();
    } else if (availability === 'false') {
      // Show unavailable vehicles - need to load all vehicles first
      await loadAllVehicles();
    }
  };

  const handleAddVehicle = () => {
    setEditingVehicle(null);
    setShowModal(true);
  };

  const handleEditVehicle = (vehicle) => {
    setEditingVehicle(vehicle);
    setShowModal(true);
  };


  const handleSaveVehicle = async (vehicleData) => {
    try {
      // Prepare vehicle data for backend
      // Map frontend camelCase to backend PascalCase
      const vehiclePayload = {
        registrationNumber: vehicleData.registrationNumber,
        model: vehicleData.model,
        brand: vehicleData.brand,
        totalSeats: vehicleData.totalSeats,
        year: vehicleData.year,
        color: vehicleData.color || '',
        busType: vehicleData.busType || '',
        description: vehicleData.description || '',
        isAvailable: vehicleData.isAvailable !== undefined ? vehicleData.isAvailable : true,
        driverId: vehicleData.driverId || '',
        contactNo: vehicleData.contactNo || '',
        seatLayout: vehicleData.seatLayout || '4x12',
        notAvailableSeat: vehicleData.notAvailableSeat || ''
      };
      
      if (editingVehicle) {
        // Update existing vehicle
        await vehicleApi.updateVehicle(editingVehicle.id, vehiclePayload);
        Swal.fire(
          'Updated!',
          'Vehicle has been updated successfully.',
          'success'
        );
      } else {
        // Create new vehicle
        await vehicleApi.createVehicle(vehiclePayload);
        Swal.fire(
          'Created!',
          'Vehicle has been created successfully.',
          'success'
        );
      }
      
      // Reload vehicles after successful save
      await loadVehicles();
      setShowModal(false);
      setEditingVehicle(null);
    } catch (err) {
      console.error('Error saving vehicle:', err);
      throw err; // Re-throw to let the modal handle the error
    }
  };

  const brands = [...new Set(vehicles.map(v => v.brand))];
  const busTypes = [...new Set(vehicles.map(v => v.busType).filter(busType => busType))];

  // Remove individual page loading since we have global loading
  // if (loading && vehicles.length === 0) {
  //   return (
  //     <div className="flex items-center justify-center min-h-screen">
  //       <BusLoading size="large" />
  //     </div>
  //   );
  // }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Vehicle Management</h1>
              <p className="text-gray-600">Manage your fleet of buses and vehicles</p>
            </div>
            <button
              onClick={handleAddVehicle}
              className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Vehicle
            </button>
          </div>
        </div>
          {/* Error Message */}
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
              <strong>Error:</strong> {error}
              <button 
                onClick={() => setError('')}
                className="float-right font-bold text-red-700 hover:text-red-900"
              >
                ×
              </button>
            </div>
          )}

        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search vehicles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Brand Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Brand</label>
              <select
                value={filterBrand}
                onChange={(e) => setFilterBrand(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Brands</option>
                {brands.map(brand => (
                  <option key={brand} value={brand}>{brand}</option>
                ))}
              </select>
            </div>

            {/* Bus Type Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Bus Type</label>
              <select
                value={filterBusType}
                onChange={(e) => setFilterBusType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Bus Types</option>
                {busTypes.map(busType => (
                  <option key={busType} value={busType}>{busType}</option>
                ))}
              </select>
            </div>

            {/* Availability Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={filterAvailability}
                onChange={(e) => handleAvailabilityFilterChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
                <option value="">All</option>
              </select>
            </div>

            {/* Clear Filters */}
            <div className="flex items-end">
              <button
                onClick={() => {
                  setSearchTerm('');
                  setFilterBrand('');
                  setFilterBusType('');
                  setFilterAvailability('');
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Clear Filters
              </button>
            </div>
          </div>

          {/* Filter Results */}
          <div className="mt-4 text-sm text-gray-600">
            Showing {filteredVehicles.length} of {totalCount} vehicles
          </div>
        </div>

        {/* Vehicles Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Vehicle Details
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredVehicles.map((vehicle) => (
                  <tr key={vehicle.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-4 px-6">
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0">
                          <Bus className="w-5 h-5 text-gray-400" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{vehicle.registrationNumber}</p>
                          <p className="text-sm text-gray-500">
                            {vehicle.brand} {vehicle.model} • {vehicle.totalSeats} seats
                            {vehicle.busType && (
                              <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                                {vehicle.busType}
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        vehicle.isAvailable ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {vehicle.isAvailable ? 'Available' : 'Not Available'}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <p className="text-sm text-gray-900">
                        {vehicle.createdAt ? new Date(vehicle.createdAt).toLocaleDateString() : 'N/A'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {vehicle.createdAt ? new Date(vehicle.createdAt).toLocaleTimeString() : ''}
                      </p>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEditVehicle(vehicle)}
                          className="text-blue-600 hover:text-blue-700 p-2 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit Vehicle"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>

          {(!filteredVehicles || filteredVehicles.length === 0) && !loading && (
            <div className="text-center py-12">
              <Bus className="mx-auto h-16 w-16 text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {(!vehicles || vehicles.length === 0) ? 'No vehicles in database' : 'No vehicles match your filters'}
              </h3>
              <p className="text-gray-500">
                {(!vehicles || vehicles.length === 0)
                  ? 'Add your first vehicle to get started.' 
                  : 'Try adjusting your search criteria or clear the filters to see all vehicles.'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Vehicle Modal */}
      <VehicleModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingVehicle(null);
        }}
        onSave={handleSaveVehicle}
        vehicle={editingVehicle}
      />
    </div>
  );
};

export default VehicleManagement;
